import { useEffect, useRef, useState, createElement } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Cards, Camera, Printer, X, GraduationCap, UsersThree, Sparkle, Bird, BookOpen } from "@phosphor-icons/react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { buildLetterPDFBlob } from "@/utils/pdfExport";
import { TASK_CARD_SETS, SETTING_LABELS, TOTAL_CARDS } from "@/data/taskCards";
import TaskSheet from "@/components/taskCards/TaskSheet";
import ScaledSheet from "@/components/taskCards/ScaledSheet";
import CoverSheet from "@/components/taskCards/CoverSheet";
import TocSheet from "@/components/taskCards/TocSheet";

const SET_ICONS = { GraduationCap, UsersThree, Sparkle, Bird };
function SetIcon({ name, ...rest }) {
  return createElement(SET_ICONS[name] || Cards, rest);
}

export default function TaskCardsPage() {
  const { lang, t } = useTranslation();
  const [active, setActive] = useState(null); // { card, color, setLabel }
  const [exportCards, setExportCards] = useState(null); // [{card,color,setLabel}]
  const [busyKey, setBusyKey] = useState(null);
  const [result, setResult] = useState(null); // { url, name } — manual download fallback
  const [progress, setProgress] = useState(null); // { done, total } — booklet render
  const [welcome, setWelcome] = useState(false); // new registrant arriving for the booklet
  const stageRef = useRef(null);
  const bookletCtaRef = useRef(null);
  const preWinRef = useRef(null);
  const inIframeRef = useRef(false);
  const filenameRef = useRef("");
  const [searchParams, setSearchParams] = useSearchParams();

  const tr = (o) => (o && (o[lang] || o.en)) || "";

  // New sign-ups land here via ?welcome=booklet — greet them and spotlight the
  // booklet CTA so they grab their free download right away.
  useEffect(() => {
    if (searchParams.get("welcome") === "booklet") {
      setWelcome(true);
      const id = setTimeout(() => {
        bookletCtaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
      const cleanup = setTimeout(() => setWelcome(false), 6000);
      searchParams.delete("welcome");
      setSearchParams(searchParams, { replace: true });
      return () => {
        clearTimeout(id);
        clearTimeout(cleanup);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kick off a Letter PDF export of one or many sheets. `cards` items may be
  // { card, color, setLabel } or special pages { type: "cover" | "toc" }.
  const startExport = (cards, filename, key) => {
    const inIframe = (() => {
      try {
        return window.top !== window.self;
      } catch {
        return true;
      }
    })();
    inIframeRef.current = inIframe;
    // No "noopener": with it the browser returns null and we lose the handle.
    preWinRef.current = inIframe ? window.open("", "_blank") : null;
    // Clear any previous ready-link before generating a new one.
    setResult((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
    setProgress(null);
    filenameRef.current = filename;
    setBusyKey(key);
    setExportCards(cards);
  };

  // The full 46-card booklet: designed cover + table of contents + every sheet.
  const startBooklet = () => {
    const items = [{ type: "cover" }, { type: "toc" }];
    TASK_CARD_SETS.forEach((set) => {
      set.cards.forEach((card) => items.push({ card, color: set.color, setLabel: tr(set.title) }));
    });
    startExport(items, `dslr-advisor-field-guide-${lang}`, "booklet");
  };

  useEffect(() => {
    if (!exportCards) return;
    let cancelled = false;
    (async () => {
      // Wait two frames for the off-screen sheets to lay out, plus fonts.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch {
          /* noop */
        }
      }
      try {
        const nodes = stageRef.current ? Array.from(stageRef.current.querySelectorAll("[data-sheet]")) : [];
        // Lower the scale for big multi-page exports to keep memory/time sane.
        const scale = nodes.length > 20 ? 1.3 : nodes.length > 3 ? 1.6 : 2;
        const onProgress = nodes.length > 6 ? (done, total) => setProgress({ done, total }) : undefined;
        const blob = await buildLetterPDFBlob(nodes, { scale, onProgress });
        if (cancelled) return;
        const safe = (filenameRef.current || "dslr-advisor").replace(/[^a-z0-9-_.]/gi, "_");
        const name = safe.endsWith(".pdf") ? safe : `${safe}.pdf`;
        const url = URL.createObjectURL(blob);

        if (preWinRef.current) {
          try {
            preWinRef.current.location.href = url;
          } catch {
            /* noop */
          }
        } else if (!inIframeRef.current) {
          const a = document.createElement("a");
          a.href = url;
          a.download = name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        setResult({ url, name });
        setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[Task Cards PDF] failed:", err);
        if (preWinRef.current) preWinRef.current.close();
      } finally {
        if (!cancelled) {
          setExportCards(null);
          setBusyKey(null);
          setProgress(null);
          preWinRef.current = null;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportCards]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="task-cards-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-8">
        <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2 flex items-center gap-3">
          <Cards weight="fill" className="w-8 h-8 text-[#F5A524]" />
          {t("tc_title")}
        </h1>
        <p className="text-[#71717A] text-base max-w-3xl">{t("tc_subtitle")}</p>
        <p className="text-[#52525B] text-sm mt-1">{TOTAL_CARDS} {t("tc_cards")} · {t("tc_open_sheet")}</p>
      </motion.div>

      {/* Full booklet lead-magnet CTA */}
      <div
        ref={bookletCtaRef}
        className={`mb-12 rounded-2xl border bg-gradient-to-br from-[#F5A524]/10 to-transparent p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 transition-all duration-500 ${welcome ? "border-[#F5A524] ring-2 ring-[#F5A524]/50 shadow-lg shadow-[#F5A524]/10" : "border-[#F5A524]/30"}`}
        data-testid="booklet-cta"
      >
        <span className="w-12 h-12 rounded-xl bg-[#F5A524]/15 flex items-center justify-center shrink-0">
          <BookOpen weight="fill" className="w-7 h-7 text-[#F5A524]" />
        </span>
        <div className="flex-1 min-w-0">
          {welcome && (
            <p className="text-[#F5A524] text-sm font-semibold mb-1" data-testid="booklet-welcome-note">
              {lang === "es" ? "🎉 ¡Bienvenido! Tu folleto gratis está aquí:" : "🎉 Welcome! Your free booklet is right here:"}
            </p>
          )}
          <h2 className="font-['Outfit'] text-white text-lg font-semibold">
            {lang === "es" ? `Guía de Campo Completa — ${TOTAL_CARDS} hojas` : `The Complete Field Guide — ${TOTAL_CARDS} sheets`}
          </h2>
          <p className="text-[#A1A1AA] text-sm">
            {lang === "es"
              ? `Descarga las ${TOTAL_CARDS} hojas en un solo PDF imprimible, con portada e índice.`
              : `Download all ${TOTAL_CARDS} cheat sheets as one printable PDF, with a cover and table of contents.`}
          </p>
        </div>
        <Button
          onClick={startBooklet}
          disabled={!!busyKey}
          className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold disabled:opacity-60 shrink-0"
          data-testid="download-booklet-btn"
        >
          <BookOpen className="w-4 h-4 mr-1.5" />
          {busyKey === "booklet"
            ? progress
              ? `${lang === "es" ? "Generando" : "Generating"} ${progress.done}/${progress.total}…`
              : t("tc_generating")
            : lang === "es"
              ? "Descargar folleto"
              : "Download full booklet"}
        </Button>
      </div>

      <div className="space-y-14">
        {TASK_CARD_SETS.map((set) => (
          <section className="space-y-4" data-testid={`task-card-set-${set.id}`} key={set.id}>
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${set.color}1A` }}>
                  <SetIcon name={set.icon} weight="fill" className="w-6 h-6" style={{ color: set.color }} />
                </span>
                <div>
                  <h2 className="font-['Outfit'] text-white text-xl font-semibold">{tr(set.title)}</h2>
                  <p className="text-[#71717A] text-sm">{tr(set.subtitle)} · {set.cards.length} {t("tc_cards")}</p>
                </div>
              </div>
              <Button
                onClick={() =>
                  startExport(
                    set.cards.map((c) => ({ card: c, color: set.color, setLabel: tr(set.title) })),
                    `task-cards-${set.id}-${lang}`,
                    `set-${set.id}`
                  )
                }
                disabled={!!busyKey}
                variant="outline"
                size="sm"
                className="border-white/15 text-[#A1A1AA] hover:text-white hover:border-white/30 disabled:opacity-60"
                data-testid={`task-card-set-download-${set.id}`}
              >
                <Printer className="w-4 h-4 mr-1.5" />
                {busyKey === `set-${set.id}` ? t("tc_generating") : t("tc_download_set")}
              </Button>
            </div>

            {/* poster wall — each item is a mini 8.5x11 sheet */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {set.cards.map((card) => (
                <button
                  key={card.id}
                  onClick={() => setActive({ card, color: set.color, setLabel: tr(set.title) })}
                  className="group block text-left"
                  data-testid={`task-card-${card.id}`}
                >
                  <div className="rounded-lg ring-1 ring-white/10 shadow-lg shadow-black/40 overflow-hidden transition-all duration-200 group-hover:ring-2 group-hover:-translate-y-1" style={{ "--tw-ring-color": `${set.color}66` }}>
                    <ScaledSheet>
                      <TaskSheet card={card} lang={lang} color={set.color} setLabel={tr(set.title)} settingLabels={SETTING_LABELS} />
                    </ScaledSheet>
                  </div>
                  <div className="flex items-center justify-between mt-2 px-0.5">
                    <span className="text-white text-sm font-medium truncate">{tr(card.title)}</span>
                    <Printer className="w-4 h-4 text-[#52525B] group-hover:text-[#F5A524] transition-colors shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      {active && (
        <SheetModal
          card={active.card}
          color={active.color}
          setLabel={active.setLabel}
          lang={lang}
          t={t}
          busy={busyKey === `card-${active.card.id}`}
          onDownload={() =>
            startExport([{ card: active.card, color: active.color, setLabel: active.setLabel }], `task-card-${active.card.id}-${lang}`, `card-${active.card.id}`)
          }
          onClose={() => setActive(null)}
        />
      )}

      {/* off-screen export stage — full-size sheets rasterized on demand */}
      <div ref={stageRef} aria-hidden="true" style={{ position: "fixed", left: -10000, top: 0, width: 816, background: "#fff" }}>
        {exportCards &&
          exportCards.map((item) => {
            if (item.type === "cover") return <CoverSheet key="cover" lang={lang} totalCards={TOTAL_CARDS} totalSets={TASK_CARD_SETS.length} />;
            if (item.type === "toc") return <TocSheet key="toc" lang={lang} startPage={3} />;
            return <TaskSheet key={item.card.id} card={item.card} lang={lang} color={item.color} setLabel={item.setLabel} settingLabels={SETTING_LABELS} />;
          })}
      </div>

      {/* PDF ready — tappable link (the reliable delivery on Safari / inside
          the sandboxed preview, where auto-download is blocked) */}
      {result && (
        <div
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 bg-[#18181B] border border-[#F5A524]/40 shadow-2xl shadow-black/60 rounded-full pl-5 pr-3 py-2.5"
          data-testid="pdf-ready-banner"
        >
          <span className="text-white text-sm font-medium whitespace-nowrap">
            {lang === "es" ? "✓ Tu PDF está listo" : "✓ Your PDF is ready"}
          </span>
          <a
            href={result.url}
            download={result.name}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold text-sm rounded-full px-4 py-2 transition-colors whitespace-nowrap"
            data-testid="pdf-ready-link"
          >
            {lang === "es" ? "Abrir / Guardar PDF" : "Open / Save PDF"}
          </a>
          <button
            onClick={() => {
              URL.revokeObjectURL(result.url);
              setResult(null);
            }}
            className="text-[#A1A1AA] hover:text-white p-1.5"
            aria-label="Close"
            data-testid="pdf-ready-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function SheetModal({ card, color, setLabel, lang, t, busy, onDownload, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="task-card-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} data-testid="task-card-modal-backdrop" />
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="relative w-full max-w-[640px] my-8"
      >
        <button onClick={onClose} className="absolute -top-2 -right-2 z-10 bg-[#18181B] border border-white/15 rounded-full text-[#A1A1AA] hover:text-white p-2" data-testid="task-card-modal-close" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        <div className="rounded-xl overflow-hidden shadow-2xl shadow-black/60 ring-1 ring-white/10">
          <ScaledSheet>
            <TaskSheet card={card} lang={lang} color={color} setLabel={setLabel} settingLabels={SETTING_LABELS} />
          </ScaledSheet>
        </div>

        <Button
          onClick={onDownload}
          disabled={busy}
          className="w-full mt-4 bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
          data-testid="task-card-download-btn"
        >
          <Printer className="w-4 h-4 mr-1.5" />
          {busy ? t("tc_generating") : t("tc_download_sheet")}
        </Button>
      </motion.div>
    </div>
  );
}
